import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import isEqual from 'lodash/isEqual';

import useAuthentication from '../hooks/useAuthentication';
import useUsergroupManagement from '../hooks/useUsergroupManagement';
import { getItem, setItem } from '../utils/indexedDB';

const CACHE_MINUTES = 1;

const UserManagementContext = createContext();

export const UserManagementProvider = ({ children }) => {
    const { currentUser, isAuthenticated, updateUser: updateCurrentUser, logout } = useAuthentication();
    
    const storageKey = 'user_management';
    const [lastUpdated, _setLastUpdated] = useState(0);
    const [allUsers, setAllUsers] = useState([]);
    // DISABLE INDEXED DB FOR NOW
    const [isInitialized, setIsInitialized] = useState(true);
    
    const {
        usergroupHasProjectAccess,
        usergroupHasRole
    } = useUsergroupManagement();

    // Clean up old localStorage key (migration)
    useEffect(() => {
        localStorage.removeItem(storageKey);
    }, []);
    
    // Load initial data from IndexedDB
    useEffect(() => {
        // DISABLE INDEXED DB FOR NOW
        //console.log("UserManagement: Initializing from IndexedDB");
        const loadFromIndexedDB = async () => {
            try {
                const data = await getItem(storageKey);
                if (data) {
                    _setLastUpdated(data.lastUpdated || 0);
                    setAllUsers(data.allUsers || []);
                    console.log("UserManagement: Loaded", data.allUsers?.length || 0, "users from IndexedDB");
                } else {
                    console.log("UserManagement: No cached data in IndexedDB");
                }
            } catch (error) {
                console.error('Failed to load from IndexedDB:', error);
            } finally {
                setTimeout(() => setIsInitialized(true), 50);
            }
        };
        
        // DISABLE INDEXED DB FOR NOW
        //loadFromIndexedDB();
    }, []);

    // Auto-reload when needed
    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        const shouldClearCache = lastUpdated + (1000 * 60 * CACHE_MINUTES) < Date.now();

        if (!isAuthenticated) {
            return;
        }
        
        if (allUsers.length === 0 || shouldClearCache) {
            reloadUsers();
        }
    }, [isInitialized, isAuthenticated]);

    // If we've been making updates to the state on a page, and user then press Save, update the IndexedDB
    const saveUserUpdates = useCallback(async () => {
        // DISABLE INDEXED DB FOR NOW
        return
        
        try {
            await setItem(storageKey, {
                lastUpdated: lastUpdated,
                allUsers: allUsers
            });
        } catch (error) {
            console.error('Failed to save user updates to IndexedDB:', error);
        }
    }, [lastUpdated, allUsers]);
    
    const updateUser = useCallback((id, updatedUserData) => {
        setAllUsers(prevUsers => 
            prevUsers.map(user =>
                user.id === id ? { ...user, ...updatedUserData } : user
            )
        );
    }, []);
    
    const updateUserAndSave = useCallback(async (id, userData) => {
        const updated = allUsers.map(i => {
            if (i.id === id) {
                return { ...i, ...userData };
            }
            return i;
        });
        setAllUsers(updated);
        // DISABLE INDEXED DB FOR NOW
//        try {
//            await setItem(storageKey, {
//                lastUpdated: lastUpdated,
//                allUsers: updated
//            });
//        } catch (error) {
//            console.error('Failed to save user updates to IndexedDB:', error);
//        }
    }, [allUsers, lastUpdated]);
    
    const reloadUsers = useCallback(() => {
        fetch('/v0/users', { credentials: 'same-origin' })
            .then(function(response) {
                if (response.status === 403) {
                    // They're logged out server-side; make sure that's true on client too
                    logout();
                    throw new Error(response.statusText);
                }
                if (!response.ok) throw response;
                return response.json();
            })
            .then(async json => {
                const all = json.results;
                const timestamp = Date.now();
                _setLastUpdated(timestamp);

                if (!isEqual(all, allUsers)) {
                    setAllUsers(all);
                }
//                try {
//                    await setItem(storageKey, {
//                        lastUpdated: timestamp,
//                        allUsers: all
//                    });
//                } catch (error) {
//                    console.error('Failed to save to IndexedDB:', error);
//                }
            })
            .catch(error => console.log("UserManagement.reloadUsers error", error));
    }, [allUsers, logout]);

    const getUserFromID = useCallback((id) => {
        const user = allUsers.find(item => item.id === id);
        if (typeof user === 'undefined') {
            return { label: '', display_name: '', id: 0, value: 0 };
        }
        return user;
    }, [allUsers]);
    
    const userHasRole = useCallback((user, requestedRole) => {
        if (!user.roles) {
            return false;
        }
        let index = user.roles.findIndex(function (role) {
            if (role.code === requestedRole) {
                return true;
            }
            if (role.code === 'full_access_admin') {
                return true;
            }
            if (role.implied_rule_codes) {
                let impliedIndex = role.implied_rule_codes.findIndex(function (impliedRoleCode) {
                    return impliedRoleCode === requestedRole;
                });
                if (impliedIndex >= 0) {
                    return true;
                }
            }
            return false;
        });
        if (index >= 0) {
            return true;
        }
        if (!user.usergroups) {
            return false;
        }
        return user.usergroups.some(usergroup => usergroupHasRole(usergroup, requestedRole));
    }, [usergroupHasRole]);

    const userHasUserRole = useCallback((user) => {
        // Need to be explicit here -- even superuser can lose it
        let roles = user.roles;
        if (!roles) {
            return false;
        }
        let index = roles.findIndex(function (role) {
            if (role.code === 'normal_user') {
                return true;
            }
            return false;
        });
        if (index >= 0) {
            return true;
        }
        return false;
    }, []);

    const userProjectAccessLabel = useCallback((user, projectID) => {
        if (userHasRole(user, 'full_access_admin')) {
            const role = user.roles.find(function (r) { return r.code === 'full_access_admin'; });
            return role.name;
        }
        const level = userProjectAccessLevel(user, projectID);
        const label_map = {
            0: '',
            1: 'Read-only',
            2: 'Member',
            3: 'CADM Controller',
        };
        return label_map[level];
    }, [userHasRole]);

    const userProjectAccessLevel = useCallback((user, projectID) => {
        const access_level = user.user_project_access.find(function (a) { return a.project_id === projectID; });
        const groupaccess_level = user.usergroup_project_access ? user.usergroup_project_access.find(function (a) { return a.project_id === projectID; }) : null;

        let level = access_level ? access_level.user_project_access_level_id : 0;
        level = groupaccess_level && groupaccess_level.usergroup_project_access_level_id > level ? groupaccess_level.usergroup_project_access_level_id : level;
        return level;
    }, []);

    const userHasProjectInformationAccess = useCallback((user, projectID, projectType) => {
        if (userHasRole(user, 'full_access_admin')) {
            return true;
        }
        if (projectType && projectType === 'Project' && userHasRole(user, 'project_controller')) {
            return true;
        }
        if (projectType && projectType === 'Opportunity' && userHasRole(user, 'opportunity_admin')) {
            return true;
        }
        if (projectType && projectType === 'Product' && userHasRole(user, 'product_controller')) {
            return true;
        }
        if (!projectType && (userHasProjectInformationAccess(user, projectID, 'Project') || userHasProjectInformationAccess(user, projectID, 'Opportunity'))) {
            return true;
        }
        return userHasProjectAccess(user, projectID);
    }, [userHasRole]);
    
    const userHasProjectAccess = useCallback((user, projectID) => {
        if (userHasRole(user, 'full_access_admin')) {
            return true;
        }
        return userHasExplicitProjectAccess(user, projectID);
    }, [userHasRole]);

    const userHasExplicitProjectAccess = useCallback((user, projectID) => {
        const access = user.user_project_access || [];
        if (access.some(a => a.project_id === projectID)) {
            return true;
        }
        const groupaccess = user.usergroup_project_access || [];
        if (groupaccess.some(a => a.project_id === projectID)) {
            return true;
        }
        return false;
    }, []);
    
    const userHasProjectAccessLevel = useCallback((user, projectID, level) => {
        if (userHasRole(user, 'full_access_admin')) {
            return true;
        }
        
        let levelMap = {
            READONLY: 1,
            MEMBER: 2,
            CADM_CONTROLLER: 3
        };
        let levelID = levelMap[level];

        let access = user.user_project_access;
        if (access && access.findIndex(function (a) { return a.project_id === projectID && a.user_project_access_level_id >= levelID; }) >= 0) {
            return true;
        }
        let groupaccess = user.usergroup_project_access;
        if (groupaccess && groupaccess.findIndex(function (a) { return a.project_id === projectID && a.usergroup_project_access_level_id >= levelID; }) >= 0) {
            return true;
        }
        return false;
    }, [userHasRole]);

    const userCanCreateOpportunity = useCallback((user) => {
        return userHasRole(user, 'opportunity_admin') || userHasRole(user, 'full_access_admin');
    }, [userHasRole]);
    
    const userCanCreateProject = useCallback((user) => {
        return userHasRole(user, 'project_admin') || userHasRole(user, 'full_access_admin');
    }, [userHasRole]);
    
    const userCanCreateProduct = useCallback((user) => {
        return userHasRole(user, 'product_admin') || userHasRole(user, 'full_access_admin');
    }, [userHasRole]);

    const userProjectIsFavourite = useCallback((user, projectID) => {
        if (!user.favourite_projects) {
            return false;
        }
        return user.favourite_projects.find(function (item) { return item.project_id === projectID; }) !== undefined;
    }, []);
    
    const userAddFavouriteProject = useCallback((user, id) => {
        fetch('/v0/account/add_favourite_project', {
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            method: 'POST',
            body: JSON.stringify({
                project_id: id,
            })
        }).then(function(response) {
            return response.json();
        }).then(function(json) {
            updateCurrentUser(json.result);
        });
    }, [updateCurrentUser]);
    
    const userRemoveFavouriteProject = useCallback((user, id) => {
        fetch('/v0/account/remove_favourite_project', {
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            method: 'POST',
            body: JSON.stringify({
                project_id: id,
            })
        }).then(function(response) {
            return response.json();
        }).then(function(json) {
            updateCurrentUser(json.result);
        });
    }, [updateCurrentUser]);

    // do we need to force user to configure 2fa
    const userMustConfigure2FA = useCallback((user) => {
        return user.is_2fa_required && !user.is_2fa_configured;
    }, []);

    const userShowNavigation = useCallback((user) => {
        if (!user.id || !user.id > 0) return false;
        if (userMustConfigure2FA(user)) return false;
        if (!user.has_company_agreed_toc) return false;
        return true;
    }, [userMustConfigure2FA]);

    const activeUsers = useMemo(
        () => allUsers.filter(user => userHasRole(user, 'normal_user')),
        [allUsers, userHasRole]
    );

    // When we need sync mode for this
    const forceReloadUsers = useCallback(async () => {
        await reloadUsers();
        
        // Then also update the user in pscState
        await fetch('/v0/users/' + currentUser.id, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.status === 403) {
                    // They're logged out server-side; make sure that's true on client too
                    logout();
                    throw new Error(response.statusText);
                }
                return response.json();
            })
            .then(function(json) {
                if (!json) {
                    return;
                }

                const reloadedUser = json.result;
                if (!isEqual(reloadedUser, currentUser)) {
                    updateCurrentUser(reloadedUser);
                }
            })
            .catch(error => console.log(error));
    }, [reloadUsers, currentUser, logout, updateCurrentUser]);

    const value = {
        isUserManagementInitialized: isInitialized,

        allUsers,
        activeUsers,
        updateUser,
        updateAllUsers: setAllUsers,
        updateUserAndSave,
        saveUserUpdates,
        reloadUsers,
        getUserFromID,
        userHasRole,
        userHasProjectAccess,
        userHasProjectAccessLevel,
        usergroupHasProjectAccess,
        userProjectAccessLevel,
        userProjectAccessLabel,
        userHasExplicitProjectAccess,
        userHasProjectInformationAccess,
        userCanCreateOpportunity,
        userCanCreateProject,
        userCanCreateProduct,
        userProjectIsFavourite,
        userAddFavouriteProject,
        userRemoveFavouriteProject,
        userMustConfigure2FA,
        userShowNavigation,

        forceReloadUsers,
    };

    return (
        <UserManagementContext.Provider value={value}>
            {children}
        </UserManagementContext.Provider>
    );
};

export const useUserManagementContext = () => {
    const context = useContext(UserManagementContext);
    if (!context) {
        throw new Error('useUserManagementContext must be used within a UserManagementProvider');
    }
    return context;
};
